// src/pages/MessageCenter.js
/** FULL INTEGRATED FILE (DESKTOP + MOBILE) **/

import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  TextField,
  Collapse,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import useAuthHook from "../hooks/useAuth";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";


// Universal modal
import CreateNewModal from "../components/modals/CreateNewModal";
import ConfirmDialog from "../components/ConfirmDialog";

// ---------------------------------------------
// TIMESTAMP HELPER
// ---------------------------------------------
function formatTimestamp(ts) {
  const date = new Date(ts);
  const now = new Date();

  const diffMs = now - date;
  const ONE_DAY = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor(diffMs / ONE_DAY);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();

  return `${mm}-${dd}-${yyyy}`;
}

// ---------------------------------------------
// DUE DATE HELPER
// ---------------------------------------------
function formatDueDate(dueDate) {
  if (!dueDate) return "";
  
  const date = new Date(dueDate);
  const now = new Date();
  
  // Reset time to midnight for accurate day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const diffMs = due - today;
  const ONE_DAY = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor(diffMs / ONE_DAY);
  
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 0) {
    // Future date - show actual date
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
  // Past date - show days ago
  return `${Math.abs(diffDays)} days ago`;
}

// ---------------------------------------------
// ACTIVITY HELPER
// ---------------------------------------------
function createActivityEntry(user, message) {
  return {
    author: user?.username || "Me",
    message: message,
    time: new Date().toLocaleString(),
  };
}

// ---------------------------------------------
// MAP NOTIFICATION SUBTYPE → BACKEND TYPE
// (kept for future use if needed)
// ---------------------------------------------
function mapNotificationType(label) {
  switch (label) {
    case "Lead":
      return "lead";
    case "Territory":
      return "territory";
    case "Route Plan":
      return "routeplan";
    case "User":
    default:
      return "system";
  }
}

// ---------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------
export default function MessageCenter() {
  const [expandedId, setExpandedId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("unread");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [noteInputByRow, setNoteInputByRow] = useState({});
  const [rescheduleDateByRow, setRescheduleDateByRow] = useState({});
  const [leadActivityByLeadId, setLeadActivityByLeadId] = useState({});
  const [leadActivityLoadingByLeadId, setLeadActivityLoadingByLeadId] = useState({});
  const [showAllActivityByRow, setShowAllActivityByRow] = useState({});
  const [savingFollowUpByRow, setSavingFollowUpByRow] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { refreshCounts } = useGlobalCounts();

  const { user } = useAuth();
  const { role } = useAuthHook();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Scope chat-thread-open layout (hide topbar, collapse main) to this page only
  useEffect(() => {
    document.body.classList.add("page-message-center");
    return () => {
      document.body.classList.remove("page-message-center");
    };
  }, []);

  // Check if user is admin
  const isAdmin = role === "Admin" || user?.is_superuser;
  const me = user?.username || "";

  const typeIconConfig = {
    Notification: {
      icon: NotificationsNoneIcon,
      bg: "#E8F1FF",
      color: "#1A73E8",
    },
    "Follow Up": {
      icon: EventNoteIcon,
      bg: "#FFF4E5",
      color: "#F57C00",
    },
    Task: {
      icon: TaskAltIcon,
      bg: "#E8F5E9",
      color: "#2E7D32",
    },
  };

  const renderTypeIcon = (category) => {
    const config = typeIconConfig[category] || typeIconConfig.Notification;
    const IconComponent = config.icon;

    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "14px",
          backgroundColor: config.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        <IconComponent sx={{ fontSize: 19, color: config.color }} />
      </Box>
    );
  };


  const subtleStatusStyles = {
    New: {
      backgroundColor: "#f1f3f4",
      color: "#D32F2F",
      borderRadius: "6px",
      fontSize: "11px",
      padding: "2px 6px",
      display: "inline-block",
    },
    "In Progress": {
      backgroundColor: "#f1f3f4",
      color: "#1976D2",
      borderRadius: "6px",
      fontSize: "11px",
      padding: "2px 6px",
      display: "inline-block",
    },
    Completed: {
      backgroundColor: "#f1f3f4",
      color: "#388E3C",
      borderRadius: "6px",
      fontSize: "11px",
      padding: "2px 6px",
      display: "inline-block",
    },
    Viewed: {
      backgroundColor: "#f1f3f4",
      color: "#555",
      borderRadius: "6px",
      fontSize: "11px",
      padding: "2px 6px",
      display: "inline-block",
    },
    "To Do": {
      backgroundColor: "#f1f3f4",
      color: "#9C27B0",
      borderRadius: "6px",
      fontSize: "11px",
      padding: "2px 6px",
      display: "inline-block",
    },
  };

  // Unique row key (ids can overlap across Notification/Follow Up/Task)
  const getRowKey = (r) => `${r.category}-${r.id}`;

  const cardMetaLineSx = {
    display: "block",
    fontSize: "0.78rem",
    lineHeight: 1.45,
    color: "text.secondary",
    whiteSpace: "normal",
    wordBreak: "break-word",
  };

  const cardTitleSx = {
    fontWeight: 700,
    lineHeight: 1.2,
    mb: 0.55,
    fontSize: { xs: "1.05rem", md: "1.15rem" },
    wordBreak: "break-word",
  };
  const filterTabs = [
    { key: "unread", label: "Unread" },
    { key: "read", label: "Read" },
    { key: "task", label: "Task" },
    { key: "all", label: "All" },
  ];

  // Allow deep linking directly to unified filters, default to unread.
  useEffect(() => {
    const requested = (searchParams.get("filter") || "").trim().toLowerCase();
    const normalized = requested || "unread";
    const allowed = new Set(["all", "unread", "read", "task"]);
    if (allowed.has(normalized) && normalized !== activeFilter) {
      setActiveFilter(normalized);
    }
  }, [searchParams, activeFilter]);

  // ---------------------------------------------------------------------
  // LOAD INBOX (Notifications + FollowUps + Tasks)
  // ---------------------------------------------------------------------
 /* ---------------- LOAD DATA ---------------- */
  const loadInbox = async () => {
    const [notifRes, followRes, taskRes] = await Promise.all([
      api.get("/notifications/"),
      api.get("/followups/"),
      api.get("/tasks/"),
    ]);

    const notifications = notifRes.data.map((n) => ({
      id: n.id,
      category: "Notification",
      title: n.type ? n.type.toUpperCase() : "SYSTEM",
      preview: n.message,
      senderName: n.created_by_name || n.created_by_username || "",
      receiverName: n.user_name || n.user_username || "",
      senderUsername: n.created_by_username || "",
      receiverUsername: n.user_username || "",
      status: n.is_read ? "Viewed" : "New",
      is_read: n.is_read,
      created_at: n.created_at,
      timestamp: formatTimestamp(n.created_at),
      details: n.message,
      activity: [],
      // FE-02: keep notifications read-only in-center unless a true deep-link target exists.
      target: null,
    }));

    const followups = followRes.data.map((f) => {
  const leadLabel =
    f.lead_first_name || f.lead_last_name
      ? `${f.lead_first_name || ""} ${f.lead_last_name || ""}`.trim()
      : f.lead_address || f.lead_name || "Unknown Lead";

  return {
    id: f.id,
    category: "Follow Up",
    lead_id: f.lead,
    title: leadLabel,              // ✅ just the lead label (no "Follow Up" prefix)
    preview: `Follow up required for ${leadLabel}`,

    senderName: f.created_by_name || f.created_by_username || "",
    receiverName: f.assigned_rep_name || f.assigned_rep_username || "Unassigned",
    senderUsername: f.created_by_username || "",
    receiverUsername: f.assigned_rep_username || "",
    status: f.completed ? "Completed" : "In Progress",
    is_read: !!(f.completed || f.acknowledged),
    acknowledged: !!f.acknowledged,
    created_at: f.created_at,
    due_date: f.due_date,
    timestamp: formatTimestamp(f.created_at),
    details: f.notes,
    activity: [],
  };
});


    const tasks = taskRes.data.map((t) => ({
      id: t.id,
      category: "Task",
      title: t.title,
      preview: t.description,
      senderName: t.created_by_name || t.created_by_username || "",
      receiverName: t.assigned_to_name || t.assigned_to_username || "",
      senderUsername: t.created_by_username || "",
      receiverUsername: t.assigned_to_username || "",
      status: t.status,
      // Keep task in Unread until it is completed.
      is_read: t.status === "Completed",
      created_at: t.created_at,
      due_date: t.due_date,
      timestamp: formatTimestamp(t.created_at),
      details: t.description,
      activity: [],
    }));

    const toTs = (row) => {
      const raw = row.created_at || row.due_date || null;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };

    const nextRows = [...notifications, ...followups, ...tasks].sort(
      (a, b) => toTs(b) - toTs(a)
    );

    setRows((prevRows) => {
      const prevByKey = new Map(
        (prevRows || []).map((r) => [`${r.category}-${r.id}`, r])
      );

      return nextRows.map((row) => {
        const key = `${row.category}-${row.id}`;
        const prev = prevByKey.get(key);

        // Persist follow-up activity thread data across polling refreshes.
        if (row.category === "Follow Up") {
          const cachedLeadActivity = row.lead_id ? leadActivityByLeadId[row.lead_id] : null;
          return {
            ...row,
            activity:
              (prev?.activity && prev.activity.length > 0)
                ? prev.activity
                : (cachedLeadActivity || row.activity || []),
          };
        }

        return {
          ...row,
          activity: (prev?.activity && prev.activity.length > 0) ? prev.activity : (row.activity || []),
        };
      });
    });
    refreshCounts();
  };

  useEffect(() => {
    if (user) loadInbox();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => loadInbox(), 25000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);





  const isReadState = (r) => {
    if (r.category === "Notification") return r.is_read || r.status === "Viewed";
    if (r.category === "Follow Up") return r.is_read || r.status === "Completed";
    if (r.category === "Task") return r.status === "Completed";
    return !!r.is_read;
  };

  const countByFilter = (filterKey) => {
    switch (filterKey) {
      case "unread":
        return rows.filter((r) => !r.is_read).length;
      case "read":
        return rows.filter((r) => isReadState(r)).length;
      case "task":
        return rows.filter((r) => r.category === "Task").length;
      case "all":
      default:
        return rows.length;
    }
  };

  /* ---------------- FILTERED ROWS ---------------- */
  const filteredRows = rows.filter((r) => {
    switch (activeFilter) {
      case "unread":
        return !r.is_read;
      case "read":
        return isReadState(r);
      case "task":
        return r.category === "Task";
      case "all":
      default:
        return true;
    }
  });

  const mapLeadActivityToThread = (events = []) =>
    events.map((event) => ({
      author: event.user || "System",
      message: event.description || "",
      time: event.timestamp
        ? new Date(event.timestamp).toLocaleString()
        : event.created_at
        ? new Date(event.created_at).toLocaleString()
        : "",
      type: event.type || event.action_type || "Activity",
    }));

  const fetchLeadActivity = async (leadId, options = {}) => {
    const { force = false } = options;
    if (!leadId) return [];

    if (!force && Array.isArray(leadActivityByLeadId[leadId])) {
      return leadActivityByLeadId[leadId];
    }

    setLeadActivityLoadingByLeadId((prev) => ({ ...prev, [leadId]: true }));
    try {
      const res = await api.get(`/leads/${leadId}/activity/`);
      const mapped = mapLeadActivityToThread(res.data || []);
      setLeadActivityByLeadId((prev) => ({ ...prev, [leadId]: mapped }));
      return mapped;
    } catch (err) {
      console.error("lead activity fetch error:", err);
      return [];
    } finally {
      setLeadActivityLoadingByLeadId((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  // ROW CLICK — EXPAND + READ LOGIC
  const handleRowClick = async (row) => {
    if (row.category === "Notification") {
      // FE-02: Notifications are read-only click-through (no expand/collapse)
      if (!row.is_read) {
        setRows((prev) =>
          prev.map((r) =>
            r.category === row.category && r.id === row.id
              ? { ...r, is_read: true, status: "Viewed" }
              : r
          )
        );
        try {
          await api.patch(`/notifications/${row.id}/`, { is_read: true });
          refreshCounts();
        } catch (err) {
          console.error("notification read update error:", err);
        }
      }

      if (row.target) {
        navigate(row.target);
      }
      return;
    }

    const rowKey = getRowKey(row);
    const opening = expandedId !== rowKey;

    if (opening && !row.is_read) {
      setRows((prev) =>
        prev.map((r) => {
          if (r.category !== row.category || r.id !== row.id) return r;

          if (r.category === "Notification") {
            return { ...r, is_read: true, status: "Viewed" };
          }

          if (r.category === "Task" && r.status === "To Do") {
            return { ...r, status: "In Progress" };
          }

          if (r.category === "Follow Up") {
            // Keep follow-ups visible in Unread while user works them.
            // They should exit the queue only when completed.
            return r;
          }

          return r;
        })
      );
    }

    setExpandedId(opening ? rowKey : null);
    if (opening && row.category === "Follow Up") {
      setRescheduleDateByRow((prev) => ({
        ...prev,
        [rowKey]: prev[rowKey] ?? (row.due_date ? String(row.due_date).split("T")[0] : ""),
      }));
      if (row.lead_id) {
        const activity = await fetchLeadActivity(row.lead_id);
        setRows((prev) =>
          prev.map((r) =>
            r.category === row.category && r.id === row.id ? { ...r, activity } : r
          )
        );
      }
    }

    try {
      if (opening && row.category === "Notification" && !row.is_read) {
        await api.patch(`/notifications/${row.id}/`, { is_read: true });
      }

      if (opening && row.category === "Task" && row.status === "To Do") {
        await api.patch(`/tasks/${row.id}/`, { status: "In Progress" });
      }
    } catch (err) {
      console.error("status update error:", err);
    }
  };

  const handleSaveFollowUpUpdate = async (row) => {
    const rowKey = getRowKey(row);
    const noteText = (noteInputByRow[rowKey] || "").trim();
    const newDate = (rescheduleDateByRow[rowKey] || "").trim();
    const currentDate = row.due_date ? String(row.due_date).split("T")[0] : "";
    const hasDateChange = !!newDate && newDate !== currentDate;
    const hasNoteChange = !!noteText;

    if (!hasDateChange && !hasNoteChange) return;

    setSavingFollowUpByRow((prev) => ({ ...prev, [rowKey]: true }));

    let noteSaved = !hasNoteChange;
    let dateSaved = !hasDateChange;

    try {
      if (hasNoteChange && row.lead_id) {
        const leadRes = await api.get(`/leads/${row.lead_id}/`);
        const currentNotes = (leadRes.data?.notes || "").trim();
        const author = user?.full_name || user?.username || "User";
        const stampedNote = `[${new Date().toLocaleString()}] ${author}: ${noteText}`;
        const nextNotes = currentNotes ? `${currentNotes}\n\n${stampedNote}` : stampedNote;
        await api.patch(`/leads/${row.lead_id}/`, { notes: nextNotes });
        noteSaved = true;
      }

      if (hasDateChange) {
        await api.patch(`/followups/${row.id}/`, {
          due_date: newDate,
          notes: `Follow-up rescheduled to ${newDate}`,
          acknowledged: true,
        });
        dateSaved = true;
      } else {
        // Persist read transition even when only notes are updated.
        await api.patch(`/followups/${row.id}/`, { acknowledged: true });
      }

      setRows((prev) =>
        prev.map((r) =>
          r.category === row.category && r.id === row.id
            ? {
                ...r,
                is_read: true,
                acknowledged: true,
                due_date: hasDateChange ? newDate : r.due_date,
                details: hasDateChange ? `Follow-up rescheduled to ${newDate}` : r.details,
              }
            : r
        )
      );

      if (noteSaved) {
        setNoteInputByRow((prev) => ({ ...prev, [rowKey]: "" }));
      }

      refreshCounts();
      if (row.lead_id) {
        const latestActivity = await fetchLeadActivity(row.lead_id, { force: true });
        setRows((prev) =>
          prev.map((r) =>
            r.category === row.category && r.id === row.id ? { ...r, activity: latestActivity } : r
          )
        );
      }
    } catch (err) {
      console.error("follow-up save update error:", err);
    } finally {
      setSavingFollowUpByRow((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const handleMarkCompleted = async (row) => {
    const prevStatus = row.status;
    setRows((prev) =>
      prev.map((r) =>
        r.category === row.category && r.id === row.id
          ? { ...r, is_read: true, status: "Completed" }
          : r
      )
    );
    setExpandedId(null);

    try {
      if (row.category === "Notification") {
        await api.patch(`/notifications/${row.id}/`, { is_read: true });
      } else if (row.category === "Follow Up") {
        await api.patch(`/followups/${row.id}/`, { completed: true });
        if (row.lead_id) {
          const latestActivity = await fetchLeadActivity(row.lead_id, { force: true });
          setRows((prev) =>
            prev.map((r) =>
              r.category === row.category && r.id === row.id
                ? { ...r, activity: latestActivity }
                : r
            )
          );
        }
      } else if (row.category === "Task") {
        await api.patch(`/tasks/${row.id}/`, { status: "Completed" });
      }
      refreshCounts();
    } catch (err) {
      console.error("mark completed error:", err);
      setRows((prev) =>
        prev.map((r) =>
          r.category === row.category && r.id === row.id
            ? { ...r, status: prevStatus }
            : r
        )
      );
    }
  };

  // Delete → ConfirmDialog
  const handleDelete = (row) => {
    setItemToDelete(row);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.category === "Notification") {
        await api.delete(`/notifications/${itemToDelete.id}/`);
      } else if (itemToDelete.category === "Follow Up") {
        await api.delete(`/followups/${itemToDelete.id}/`);
      } else if (itemToDelete.category === "Task") {
        await api.delete(`/tasks/${itemToDelete.id}/`);
      }

      setRows((prev) =>
        prev.filter(
          (r) => r.category !== itemToDelete.category || r.id !== itemToDelete.id
        )
      );
      if (expandedId === getRowKey(itemToDelete)) {
        setExpandedId(null);
      }

      await loadInbox();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete item. Please try again.");
    } finally {
      setConfirmDelete(false);
      setItemToDelete(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "100dvh", md: "100vh" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          p: 3,
          px: { xs: 1, sm: 3 },
          height: "100%",
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.25,
            mb: 2,
          }}
        >
          <Typography variant="h4" fontWeight={700}>
            Message Center
          </Typography>
          <Button
            data-tour="message-center-create-new"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "14px",
              px: 2,
              py: 1,
              flexShrink: 0,
              whiteSpace: "nowrap",
              bgcolor: "#1a73e8",
              "&:hover": { bgcolor: "#1663c7" },
            }}
            onClick={() => setCreateModalOpen(true)}
          >
            Create New
          </Button>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: "wrap" }}>
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.key;
            return (
              <Button
                key={tab.key}
                size="small"
                variant={isActive ? "contained" : "outlined"}
                onClick={() => {
                  setActiveFilter(tab.key);
                  setSearchParams({ filter: tab.key }, { replace: true });
                  setExpandedId(null);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "14px",
                  fontWeight: isActive ? 700 : 600,
                  minHeight: 34,
                  px: 1.5,
                }}
              >
                {tab.label} ({countByFilter(tab.key)})
              </Button>
            );
          })}
        </Stack>

          <Box data-tour="message-center-inbox">
          {filteredRows.map((row) => (
    <Paper
      key={getRowKey(row)}
      sx={{
        mb: 2,
        p: { xs: 1.75, sm: 2 },
        borderRadius: 2,
        cursor: "pointer",
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(30,41,59,0.85)"
            : "background.paper",
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 6px 18px rgba(0,0,0,0.6)"
            : "0 4px 12px rgba(0,0,0,0.12)",
        "&:hover": {
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(51,65,85,0.9)"
              : "#f7f9fa",
        },
      }}
      onClick={() => handleRowClick(row)}
    >

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "flex-start" },
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {!row.is_read && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#1a73e8",
                        mt: 1.05,
                        flexShrink: 0,
                      }}
                    />
                  )}

                  {renderTypeIcon(row.category)}

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={cardTitleSx}>
                      {row.category}: {row.title}
                    </Typography>

                    <Typography sx={cardMetaLineSx}>
                      Created by {row.senderName || row.sender || "Unknown"} →{" "}
                      {row.receiverName || row.receiver || "Unknown"}
                    </Typography>

                    {row.due_date && (
                      <Typography sx={cardMetaLineSx}>
                        Due: {formatDueDate(row.due_date)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "space-between", sm: "flex-start" },
                    gap: 1,
                    flexShrink: 0,
                    ml: { xs: 0, sm: 1 },
                    pl: { xs: 0, sm: 0 },
                  }}
                >
                  <Box sx={subtleStatusStyles[row.status] || subtleStatusStyles["New"]}>
                    {row.status}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.timestamp}
                  </Typography>
                </Box>
              </Box>

              <Collapse in={row.category !== "Notification" && expandedId === getRowKey(row)}>
                <Box
                  sx={{
                    maxHeight: expandedId === getRowKey(row)
                      ? "calc(100vh - 300px)" 
                      : "none",
                    overflowY: expandedId === getRowKey(row)
                      ? "auto" 
                      : "visible",
                    pr: 1,
                  }}
                >
                  {/* ------------------------------------------------------Action panel should not collapse on click */}
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Divider sx={{ my: 2 }} />

                    {row.category === "Follow Up" ? (
                      <>
                        <Typography fontWeight={700} mb={1}>
                          Add note
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          placeholder="Add note to lead history."
                          value={noteInputByRow[getRowKey(row)] || ""}
                          onChange={(e) =>
                            setNoteInputByRow((prev) => ({ ...prev, [getRowKey(row)]: e.target.value }))
                          }
                          sx={{ mb: 2 }}
                        />

                        <Typography fontWeight={700} mb={1}>
                          Reschedule
                        </Typography>
                        <TextField
                          fullWidth
                          type="date"
                          value={rescheduleDateByRow[getRowKey(row)] || ""}
                          onChange={(e) =>
                            setRescheduleDateByRow((prev) => ({ ...prev, [getRowKey(row)]: e.target.value }))
                          }
                          InputLabelProps={{ shrink: true }}
                          sx={{ mb: 2 }}
                        />

                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Button
                            variant="contained"
                            sx={{ textTransform: "none", color: theme.palette.mode === "dark" ? "#fff" : undefined }}
                            onClick={() => handleSaveFollowUpUpdate(row)}
                            disabled={!!savingFollowUpByRow[getRowKey(row)]}
                          >
                            {savingFollowUpByRow[getRowKey(row)] ? "Saving..." : "Save Update"}
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              bgcolor: "#1a73e8",
                              color: "#fff",
                              "&:hover": { bgcolor: "#1663c7" },
                            }}
                            onClick={() => {
                              const goToLead = async () => {
                                try {
                                  await api.patch(`/followups/${row.id}/`, { acknowledged: true });
                                } catch (err) {
                                  console.error("follow-up acknowledge on view lead failed:", err);
                                } finally {
                                  setRows((prev) =>
                                    prev.map((r) =>
                                      r.category === row.category && r.id === row.id
                                        ? { ...r, is_read: true, acknowledged: true }
                                        : r
                                    )
                                  );
                                  refreshCounts();
                                  if (row.lead_id) {
                                    navigateToLeadDetail(navigate, row.lead_id, {
                                      pathname: location.pathname,
                                      search: location.search,
                                      label: "Message Center",
                                    });
                                  }
                                }
                              };
                              goToLead();
                            }}
                          >
                            View Lead
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="contained"
                              startIcon={<DeleteIcon />}
                              sx={{
                                textTransform: "none",
                                bgcolor: "#d32f2f",
                                color: "#fff",
                                "&:hover": { bgcolor: "#c62828" },
                              }}
                              onClick={() => handleDelete(row)}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Update task status as you move through workflow.
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              color: theme.palette.mode === "dark" ? "#fff" : undefined,
                              bgcolor: "#1976d2",
                              "&:hover": { bgcolor: "#1565c0" },
                            }}
                            onClick={async () => {
                              try {
                                await api.patch(`/tasks/${row.id}/`, { status: "In Progress" });
                                setRows((prev) =>
                                  prev.map((r) =>
                                    r.category === row.category && r.id === row.id
                                      ? { ...r, status: "In Progress" }
                                      : r
                                  )
                                );
                                refreshCounts();
                              } catch (err) {
                                console.error("task in-progress update error:", err);
                              }
                            }}
                          >
                            In Progress
                          </Button>
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: "none",
                              bgcolor: "#2e7d32",
                              color: "#fff",
                              "&:hover": { bgcolor: "#1b5e20" },
                            }}
                            onClick={() => handleMarkCompleted(row)}
                          >
                            Mark Completed
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="contained"
                              startIcon={<DeleteIcon />}
                              sx={{
                                textTransform: "none",
                                bgcolor: "#d32f2f",
                                color: "#fff",
                                "&:hover": { bgcolor: "#c62828" },
                              }}
                              onClick={() => handleDelete(row)}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* ----------------------------------------------------Create New Modal */}
      <CreateNewModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={loadInbox}
      />

      {/* --------------------------------------------------Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={`Delete ${itemToDelete?.category || "Item"}`}
        message={`Are you sure you want to delete this ${
          itemToDelete?.category?.toLowerCase() || "item"
        }?`}
        confirmText="Delete"
        cancelText="Cancel"
        itemName={itemToDelete?.title}
        severity="error"
      />
    </Box>
  );
}

/** END OF FILE **/